import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  enumFreightTerm,
  enumPaymentTerm,
  enumPurchaseOrderStatus,
  enumUnit,
  FreightTerm,
  MoneyInput,
  PaymentTerm,
  PurchaseOrderStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleDialog, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { PurchaseOrderWizardStepContractInfo } from './PurchaseOrderWizardStepContractInfo';
import { PurchaseOrderWizardStepMaterials } from './PurchaseOrderWizardStepMaterials';

import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';
import * as state from '~/modules/trade/_state/purchaseOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { toMoney, toMoneyInput, zeroMoney } from '~/modules/trade/utils/money';

type PurchaseOrderWizardProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (createdOrderId?: string) => void;
  draftPurchaseOrderId?: string;
};

interface Step {
  number: number;
  title: string;
}

interface PurchaseOrderData {
  documentNumber: string;
  status: PurchaseOrderStatus;
  startDate?: CalendarDate;
  endDate?: CalendarDate;
  accountId: string;
  counterpartyReferenceNumber?: string | null;
  accountContactId?: string | null;
  facilityId: string;
  accountRepresentativeId?: string | null;
  accountAddressId: string;
  paymentCurrency: string;
  freightTerm: FreightTerm;
  paymentTerm: PaymentTerm;
  notes?: string | null;
  completionNotes?: string | null;
  lineItems?: Array<{
    id?: string;
    purchaseOrderId?: string;
    itemId: string;
    unit: Unit;
    quantity: number;
    unitPrice: MoneyInput;
    totalPrice: MoneyInput;
  }>;
}

const STEP_COMPONENTS: Record<number, React.ComponentType> = {
  1: PurchaseOrderWizardStepContractInfo,
  2: PurchaseOrderWizardStepMaterials,
};

export const PurchaseOrderWizard = ({
  isOpen,
  onOpenChange,
  onSuccess,
  draftPurchaseOrderId: draftPurchaseOrderIdProp,
}: PurchaseOrderWizardProps) => {
  const t = useTranslations();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: existingPurchaseOrder, isLoading: _isLoadingExisting } =
    useAtomValue(state.$existingPurchaseOrder);

  const [currentStep, setCurrentStep] = useAtom(state.$currentStep);
  const [form] = useAtom(state.$purchaseOrderForm);
  const [, resetForm] = useAtom(state.$resetPurchaseOrderForm);
  const [, validateForm] = useAtom(state.$validatePurchaseOrderForm);
  const [, validateFormDraft] = useAtom(state.$validatePurchaseOrderFormDraft);
  const [, setPurchaseOrderForm] = useAtom(state.$setPurchaseOrderForm);
  const [, setPurchaseOrderId] = useAtom(state.$setPurchaseOrderId);
  const [currentDraftId, setCurrentDraftId] = useAtom(state.$currentDraftId);

  const createPurchaseOrderMutation = useSetAtom(state.$createPurchaseOrder);
  const updatePurchaseOrderMutation = useSetAtom(state.$updatePurchaseOrder);
  const nextDocumentNumberMutation = useSetAtom(state.$nextDocumentNumber);

  const toast = useToast();

  const draftPurchaseOrderId =
    draftPurchaseOrderIdProp ??
    (form.id && !form.id.startsWith('temp-') ? form.id : null);

  React.useEffect(() => {
    if (isOpen && !draftPurchaseOrderIdProp) {
      const tempId = `temp-${Date.now()}`;
      setCurrentDraftId(tempId);
      setPurchaseOrderId(tempId);

      nextDocumentNumberMutation({}).then((data) => {
        setPurchaseOrderForm({
          purchaseOrderNumber: data.tradePurchaseOrderNextDocumentNumber,
        });
      });
    } else if (isOpen && draftPurchaseOrderIdProp) {
      setCurrentDraftId(draftPurchaseOrderIdProp);
      setPurchaseOrderId(draftPurchaseOrderIdProp);
    }
  }, [
    isOpen,
    draftPurchaseOrderIdProp,
    setCurrentDraftId,
    setPurchaseOrderForm,
    nextDocumentNumberMutation,
    setPurchaseOrderId,
  ]);

  React.useEffect(() => {
    if (
      existingPurchaseOrder &&
      isOpen &&
      draftPurchaseOrderId &&
      currentDraftId === draftPurchaseOrderId &&
      form.id === draftPurchaseOrderId &&
      form.purchaseOrderNumber !== existingPurchaseOrder.documentNumber
    ) {
      const purchaseOrder = existingPurchaseOrder;

      setPurchaseOrderForm({
        id: draftPurchaseOrderId,
        purchaseOrderNumber: purchaseOrder.documentNumber ?? '',
        facilityId: purchaseOrder.facility?.id ?? '',
        supplierId: purchaseOrder.account?.id ?? '',
        contactId: purchaseOrder.contact?.id ?? '',
        accountRepresentativeId: purchaseOrder.accountRepresentative?.id ?? '',
        purchaseOrderStartDate: purchaseOrder.startDate
          ? maybeParseCalendarDate(purchaseOrder.startDate.toString())
          : undefined,
        purchaseOrderEndDate: purchaseOrder.endDate
          ? maybeParseCalendarDate(purchaseOrder.endDate.toString())
          : undefined,
        counterpartyReferenceNumber:
          purchaseOrder.counterpartyReferenceNumber ?? '',
        supplierAddressId: purchaseOrder.accountAddress?.id ?? '',
        paymentCurrency: purchaseOrder.paymentCurrency ?? '',
        freightTerm: purchaseOrder.freightTerm || enumFreightTerm.FOB,
        paymentTerm: purchaseOrder.paymentTerm || enumPaymentTerm.Net30,
        notes: purchaseOrder.notes ?? '',
        completionNotes: purchaseOrder.completionNotes ?? '',
        lineItems:
          purchaseOrder.lineItems?.map((item) => ({
            id: item.id,
            itemId: item.itemId ?? '',
            purchaseOrderId: item.purchaseOrderId,
            unit: item.unit ?? enumUnit.Each,
            quantity: Number(item.quantity),
            unitPrice: item.unitPrice ? toMoney(item.unitPrice) : zeroMoney(),
            totalPrice: item.totalPrice
              ? toMoney(item.totalPrice)
              : zeroMoney(),
          })) ?? [],
        isSubmitting: false,
      });
    }
  }, [
    existingPurchaseOrder,
    isOpen,
    setPurchaseOrderForm,
    form.id,
    form.purchaseOrderNumber,
    draftPurchaseOrderId,
    currentDraftId,
    form.lineItems.length,
  ]);

  const wizardSteps: Step[] = React.useMemo(
    () => [
      {
        number: 1,
        title: t.purchaseOrdersView.wizard.steps.contractInfo,
      },
      {
        number: 2,
        title: t.purchaseOrdersView.wizard.steps.materials,
      },
    ],
    [t],
  );

  const isFirstStep = currentStep === 1;
  const isEditingDraft = !!draftPurchaseOrderId;
  const isMutationPending = form.isSubmitting || isSubmitting;

  const getFieldName = React.useCallback(
    (key: string) => {
      switch (key) {
        case 'purchaseOrderNumber':
          return t.purchaseOrdersView.wizard.validation.fieldNames
            .purchaseOrderNumber;
        case 'facilityId':
          return t.purchaseOrdersView.wizard.validation.fieldNames.facilityId;
        case 'supplierId':
          return t.purchaseOrdersView.wizard.validation.fieldNames.supplierId;
        case 'purchaseOrderStartDate':
          return t.purchaseOrdersView.wizard.validation.fieldNames
            .purchaseOrderStartDate;
        case 'supplierAddressId':
        case 'accountAddressId':
          return t.purchaseOrdersView.wizard.validation.fieldNames
            .accountAddressId;
        case 'paymentCurrency':
          return t.purchaseOrdersView.wizard.validation.fieldNames
            .paymentCurrency;
        case 'paymentTerm':
          return t.purchaseOrdersView.wizard.validation.fieldNames.paymentTerm;
        case 'freightTerm':
          return t.purchaseOrdersView.wizard.validation.fieldNames.freightTerm;
        default:
          return key;
      }
    },
    [t],
  );

  const showValidationError = React.useCallback(
    (errors: Record<string, unknown>) => {
      const fieldNames = Object.keys(errors)
        .filter((key) => !key.startsWith('lineItem|'))
        .map(getFieldName);

      if (
        fieldNames.length === 0 &&
        Object.keys(errors).some((key) => key.startsWith('lineItem|'))
      ) {
        toast.error(t.purchaseOrdersView.wizard.validation.lineItemsRequired);
        return;
      }

      const errorMessage =
        fieldNames.length === 1
          ? t.purchaseOrdersView.wizard.validation.requiredField(fieldNames[0])
          : t.purchaseOrdersView.wizard.validation.requiredFields(
              fieldNames.join(', '),
            );

      toast.error(errorMessage);
    },
    [getFieldName, t, toast],
  );

  const mapLineItems = React.useCallback(
    () =>
      form.lineItems.map((item) => ({
        ...(item.id.startsWith('temp-') ? {} : { id: item.id }),
        itemId: item.itemId ?? '',
        ...(item.purchaseOrderId
          ? { purchaseOrderId: item.purchaseOrderId }
          : {}),
        unit: (item.unit as Unit) ?? enumUnit.Pounds,
        quantity: item.quantity ?? 0,
        unitPrice: toMoneyInput(item.unitPrice),
        totalPrice: toMoneyInput(item.totalPrice),
      })),
    [form.lineItems],
  );

  const buildPurchaseOrderData = React.useCallback(
    (status: PurchaseOrderStatus): PurchaseOrderData => ({
      documentNumber: form.purchaseOrderNumber ?? '',
      status,
      startDate: form.purchaseOrderStartDate ?? undefined,
      endDate: form.purchaseOrderEndDate ?? undefined,
      accountId: form.supplierId ?? '',
      counterpartyReferenceNumber: form.counterpartyReferenceNumber,
      accountContactId: form.contactId,
      facilityId: form.facilityId ?? '',
      accountRepresentativeId: form.accountRepresentativeId,
      accountAddressId: form.supplierAddressId ?? '',
      paymentCurrency: form.paymentCurrency ?? '',
      freightTerm: form.freightTerm ?? enumFreightTerm.FOB,
      paymentTerm: form.paymentTerm ?? enumPaymentTerm.Net30,
      notes: form.notes,
      completionNotes: form.completionNotes,
      lineItems: currentStep === 2 ? mapLineItems() : undefined,
    }),
    [form, mapLineItems, currentStep],
  );

  const executePurchaseOrderMutation = React.useCallback(
    async (
      purchaseOrderData: PurchaseOrderData,
      isDraft: boolean,
    ): Promise<string | undefined> => {
      try {
        setIsSubmitting(true);

        if (isEditingDraft) {
          await updatePurchaseOrderMutation({
            id: draftPurchaseOrderId,
            ...purchaseOrderData,
          });
          return draftPurchaseOrderId;
        } else {
          const results = await createPurchaseOrderMutation({
            ...purchaseOrderData,
            status: purchaseOrderData.status as PurchaseOrderStatus,
            accountId: form.supplierId as string,
            facilityId: form.facilityId as string,
            paymentCurrency: form.paymentCurrency as string,
            freightTerm: form.freightTerm as FreightTerm,
            paymentTerm: form.paymentTerm as PaymentTerm,
            accountAddressId: form.supplierAddressId as string,
            lineItems: isDraft ? [] : purchaseOrderData.lineItems,
          });

          const createdId = results.tradePurchaseOrderCreate.id;

          setPurchaseOrderForm({ id: createdId });

          if (!isDraft) {
            setPurchaseOrderId(createdId);
          }

          return createdId;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isEditingDraft,
      draftPurchaseOrderId,
      updatePurchaseOrderMutation,
      createPurchaseOrderMutation,
      form,
      setPurchaseOrderId,
      setPurchaseOrderForm,
    ],
  );

  const handleClose = React.useCallback(() => {
    resetForm();
    setCurrentStep(1);
    onOpenChange(false);
  }, [resetForm, onOpenChange, setCurrentStep]);

  const handleNext = React.useCallback(() => {
    const validation = validateForm();
    if (validation.isValid && currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    } else if (!validation.isValid) {
      showValidationError(validation.errors);
    }
  }, [
    validateForm,
    currentStep,
    wizardSteps.length,
    showValidationError,
    setCurrentStep,
  ]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  const handleContinueAsDraft = React.useCallback(async () => {
    const validation = validateFormDraft();
    if (!validation.isValid) {
      showValidationError(validation.errors);
      return;
    }

    try {
      const purchaseOrderData = buildPurchaseOrderData(
        enumPurchaseOrderStatus.Draft,
      );
      const createdId = await executePurchaseOrderMutation(
        purchaseOrderData,
        true,
      );

      const isLastStep = currentStep === wizardSteps.length;

      if (isLastStep) {
        onSuccess?.(createdId);
        handleClose();
      } else {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(t.purchaseOrdersView.wizard.errors.saveFailed);
    }
  }, [
    validateFormDraft,
    showValidationError,
    buildPurchaseOrderData,
    executePurchaseOrderMutation,
    currentStep,
    wizardSteps.length,
    onSuccess,
    handleClose,
    toast,
    t,
    setCurrentStep,
  ]);

  const handleSubmit = React.useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      showValidationError(validation.errors);
      return;
    }

    try {
      const purchaseOrderData = buildPurchaseOrderData(
        enumPurchaseOrderStatus.Open,
      );

      const createdId = await executePurchaseOrderMutation(
        purchaseOrderData,
        false,
      );

      onSuccess?.(createdId);
      handleClose();
    } catch (error) {
      console.error('Error creating/updating purchase order:', error);
      toast.error(t.purchaseOrdersView.wizard.errors.saveFailed);
    }
  }, [
    validateForm,
    showValidationError,
    buildPurchaseOrderData,
    executePurchaseOrderMutation,
    onSuccess,
    handleClose,
    toast,
    t,
  ]);

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <ConsoleDialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      askBeforeClosing={isEditingDraft}
      className="@PurchaseOrderWizard"
      size="fill"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>
              {isEditingDraft
                ? t.purchaseOrdersView.wizard.editTitle
                : t.purchaseOrdersView.wizard.title}
            </span>
            <span className="font-light">
              {form.purchaseOrderNumber ?? 'Draft'}
            </span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={StepComponent ? <StepComponent /> : null}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isPending={isMutationPending}
              >
                {t.purchaseOrdersView.wizard.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            {isFirstStep ? (
              <>
                <Button
                  color="neutral"
                  variant="outline"
                  onPress={handleContinueAsDraft}
                  isPending={isMutationPending}
                  isLoading={isMutationPending}
                >
                  {t.purchaseOrdersView.wizard.buttons.continueAsDraft}
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleNext}
                  isPending={isMutationPending}
                >
                  {t.purchaseOrdersView.wizard.buttons.next}
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="neutral"
                  variant="outline"
                  onPress={handleContinueAsDraft}
                  isPending={isMutationPending}
                  isLoading={isMutationPending}
                >
                  {t.purchaseOrdersView.wizard.buttons.saveAsDraft}
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleSubmit}
                  isPending={isMutationPending}
                  isLoading={isMutationPending}
                >
                  {t.purchaseOrdersView.wizard.buttons.savePurchaseOrder}
                </Button>
              </>
            )}
          </div>
        </div>
      }
    />
  );
};
